/**
 * The customer download API: start a ZIP job, poll it, refresh its expiring
 * links, and retry a part that failed.
 */
import { api } from './client';
import type { DownloadPart, DownloadRequest, JobStatus } from '../types/roll';

/** A download job as the status and refresh endpoints return it. */
export interface DownloadJob {
  job_id: string;
  status: JobStatus;
  /** The ZIP's link on a single-part job. */
  presigned_url?: string;
  presigned_expires_at?: string;
  /** Number of ZIPs. Absent or 1 for a single-part job. */
  part_count?: number;
  /** One entry per ZIP, in ascending `part_no`. */
  parts?: DownloadPart[];
  error?: string;
  scan_errors?: Array<{ scan_id: string; error: string }>;
}

export const downloads = {
  start: (request: DownloadRequest) => api.post<{ job_id: string }>('/api/v1/downloads', request),
  status: (jobId: string) => api.get<DownloadJob>(`/api/v1/downloads/${jobId}`),
  /** Re-sign the job's links, which expire an hour after they're issued. */
  refresh: (jobId: string) => api.post<DownloadJob>(`/api/v1/downloads/${jobId}/refresh`),
  retryPart: (jobId: string, partNo: number) =>
    api.post<void>(`/api/v1/downloads/${jobId}/parts/${partNo}/retry`),
};

/** True when a job was split into more than one ZIP. */
export function isMultiPart(job: Pick<DownloadJob, 'parts'>): boolean {
  return !!job.parts && job.parts.length > 1;
}

/** True when a presigned link has expired, or was never issued. */
export function isUrlExpired(expiresAt: string | undefined, now = Date.now()): boolean {
  if (!expiresAt) return true;
  return new Date(expiresAt).getTime() <= now;
}
